import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  selectCurrentProject,
  selectPointsRecords,
  selectTaskSubmissions,
  selectXpRecords,
} from '../../modules/missionControl/selectors'
import { selectIsEthRaise } from '../../modules/web3/selectors'
import { wrapRewardDisplay } from '../../page_components/Home/useUpcomingProjects'
import { selectIsDrops } from '../../modules/miscUi/selectors'
import { useTaskVariableRewardProgress } from './TaskVariableRewardProgress'
import { showDecimalsIfNeeded } from '../../page_components/Dashboard/Allocations/InvestmentInfo'
import { TierVariableRewardMetaData } from './types'

export const Text = styled.div`
  color: ${({ theme }) => theme.colors.brandPrimary};
  text-align: center;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 20.941px;
  font-style: normal;
  font-weight: 300;
  line-height: 1.2;
  letter-spacing: 6.282px;
  text-transform: uppercase;
`

function getMaxRewardForTierIndex(
  tiers: TierVariableRewardMetaData['tiers'],
  index: number,
) {
  const [tierReward, tierMetric, tierMaximum] = tiers[index]
  const previousTier = tiers[index - 1]
  const previousTierMaximum = previousTier ? previousTier[2] : 0
  return Math.floor(
    tierReward * ((tierMaximum - previousTierMaximum) / tierMetric),
  )
}

export const useGetFormattedTaskRewardLabels = ({
  taskId,
}: {
  taskId: number
}) => {
  const currentProject = useSelector(selectCurrentProject)
  const taskSubmissions = useSelector(selectTaskSubmissions)
  const xpRecords = useSelector(selectXpRecords)
  const pointRecords = useSelector(selectPointsRecords)

  const task = currentProject.tasks.find((task) => task.id === taskId)
  const coreTask = task?.subtasks?.[0]
  const currentSubmissions = taskSubmissions?.[coreTask?.id]

  const hasMoreThanOneSubmission = currentSubmissions?.length > 1

  // TODO: refactor and move this logic from this hook.
  const xpRecord =
    currentSubmissions?.length &&
    !hasMoreThanOneSubmission &&
    xpRecords?.find(
      (r) => r.taskSubmissionId === currentSubmissions[0].submissionId,
    )
  const pointRecord =
    currentSubmissions?.length &&
    !hasMoreThanOneSubmission &&
    pointRecords?.find(
      (r) => r.attachmentId === currentSubmissions[0].submissionId.toString(),
    )

  const { pointReward } = task
  const { metaData } = coreTask

  const reward = xpRecord?.xp || pointRecord?.amount || pointReward

  const isEthRaise = useSelector(selectIsEthRaise)
  const isDrops = useSelector(selectIsDrops)
  const totalTaskPoints = useTaskVariableRewardProgress({ taskId })

  const getFormattedReward = useCallback(
    (overrideReward?: number, rewardDisplay?: string) => {
      let formattedReward = undefined
      let formattedMaxReward = undefined
      let formattedTiers = undefined

      const fixedReward = overrideReward ?? reward

      let dynamicReward = undefined
      let dynamicMetric = undefined
      let dynamicMaximum = undefined
      if (metaData && 'rewardDistributionType' in metaData) {
        if (metaData?.rewardDistributionType === 'linear') {
          dynamicReward = metaData.rewardPerMetric
          dynamicMetric = metaData.metric
          dynamicMaximum = metaData.maxReward
        } else if (metaData?.rewardDistributionType === 'tiers') {
          const tiers = metaData.tiers
          const currentTierIndex = tiers.findIndex((_tier, index) => {
            const sumOfPreviousTiersMaximum = tiers
              .slice(0, index)
              .reduce(
                (acc, __tier, i) => acc + getMaxRewardForTierIndex(tiers, i),
                0,
              )

            const maxRewardForTier = getMaxRewardForTierIndex(tiers, index)

            const maximumUpUntilCurrentTier =
              sumOfPreviousTiersMaximum + maxRewardForTier

            return totalTaskPoints <= maximumUpUntilCurrentTier
          })

          const currentTier = tiers[currentTierIndex] ?? tiers[0]
          const tiersLength = tiers.length

          const [tierReward, tierMetric, _tierMaximum] = currentTier
          dynamicReward = tierReward
          dynamicMetric = tierMetric
          dynamicMaximum = tiers.reduce(
            (acc, __tier, i) => acc + getMaxRewardForTierIndex(tiers, i),
            0,
          )
          formattedTiers = `Tier ${
            (currentTierIndex < 0 ? 0 : currentTierIndex) + 1
          }/${tiersLength}`
        }
        const { metricName: metricNameRaw } = metaData

        const metricName =
          dynamicMetric === 1
            ? metricNameRaw
            : metricNameRaw.toLowerCase() === 'vault' ||
              metricNameRaw.toLowerCase() === 'view'
            ? `${metricNameRaw}s`
            : metricNameRaw

        formattedReward = `${wrapRewardDisplay({
          value: dynamicReward,
          isEthRaise,
          rewardDisplay,
          isDrops,
        })} per ${showDecimalsIfNeeded(
          dynamicMetric,
          isEthRaise ? 7 : 2,
        )} ${metricName}`
        formattedMaxReward = `up to ${wrapRewardDisplay({
          value: dynamicMaximum,
          isEthRaise,
          rewardDisplay,
          isDrops,
        })}`
      } else {
        if (metaData && 'maxReward' in metaData) {
          formattedMaxReward = `up to ${wrapRewardDisplay({
            value: metaData.maxReward,
            isEthRaise,
            rewardDisplay,
            isDrops,
          })}`
        }
        
        formattedReward = `${wrapRewardDisplay({
          value: fixedReward,
          isEthRaise,
          rewardDisplay,
          isDrops,
        })}`
      }

      return {
        formattedReward,
        formattedMaxReward,
        formattedTiers,
      }
    },
    [isDrops, isEthRaise, metaData, reward, totalTaskPoints],
  )

  return useCallback(
    ({
      overrideReward,
      rewardDisplay,
      includePlus = false,
    }: {
      overrideReward?: number
      rewardDisplay?: string
      includePlus?: boolean
    } = {}) => {
      const prefix = includePlus ? '+' : ''
      const { formattedReward, formattedMaxReward, formattedTiers } =
        getFormattedReward(overrideReward, rewardDisplay)
      return {
        formattedReward: `${prefix}${formattedReward}`,
        formattedMaxReward,
        formattedTiers,
      }
    },
    [getFormattedReward],
  )
}

const TaskAllocationAmount = ({
  taskId,
  rewardDisplay,
  isRecurringTask,
}: {
  taskId: number
  rewardDisplay?: string
  isRecurringTask?: boolean
}) => {
  const getFormattedTaskAllocationLabels = useGetFormattedTaskRewardLabels({
    taskId,
  })

  const { formattedReward } = getFormattedTaskAllocationLabels({
    rewardDisplay,
  })

  if (!formattedReward) return null


  if (isRecurringTask) {
    return <Text>{formattedReward} per complete</Text>
  }

  return <Text>{formattedReward}</Text>
}

export const TaskAllocationMaxAmount = ({
  taskId,
  rewardDisplay,
}: {
  taskId: number
  rewardDisplay?: string
}) => {
  const getFormattedTaskAllocationLabels = useGetFormattedTaskRewardLabels({
    taskId,
  })

  const { formattedMaxReward } = getFormattedTaskAllocationLabels({
    rewardDisplay,
  })

  if (!formattedMaxReward) return null

  return <Text>{formattedMaxReward}</Text>
}

export default TaskAllocationAmount
